import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  IconButton,
  Collapse,
  Box,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import THIRD_PARTY_TYPES from '@/components/jobs/JobForm/thirdPartyTypes';

export default function DataReview({ data }) {
  const [expanded, setExpanded] = useState(true);
  const project_datastores = useSelector(
    (state) => state.projectDatastores || [],
  );
  const volumes = useSelector((state) => state.volumes || []);
  const project_data_connectors = useSelector(
    (state) => state.projectDataConnectors || [],
  );
  const project_services = useSelector((state) => state.projectServices || []);

  if (!data) {
    return null;
  }

  const {
    datasets,
    input_type,
    input_uri,
    input_options,
    output_type,
    output_uri,
    output_options,
    volumes: data_volumes,
    data_connectors,
    private_services,
  } = data;

  let input_datastore_name, output_datastore_name;

  if (input_type === THIRD_PARTY_TYPES.REGIONAL.value) {
    const datastore = project_datastores.find(
      (datastore) => datastore.id === input_uri,
    );
    if (datastore) {
      input_datastore_name = datastore.name;
    }
  }

  if (output_type === THIRD_PARTY_TYPES.REGIONAL.value) {
    const datastore = project_datastores.find(
      (datastore) => datastore.id === output_uri,
    );
    if (datastore) {
      output_datastore_name = datastore.name;
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          p: 1,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size='small'>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
        <Typography variant='h6'>Data</Typography>
      </Box>
      <Collapse in={expanded}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            {datasets && datasets.length ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant='h6' gutterBottom>
                  Input
                </Typography>
                {datasets.length === 1 ? (
                  <Typography>
                    Load dataset <b>{datasets[0].name}</b> into{' '}
                    <span
                      style={{
                        fontFamily: '"Courier New", Courier, monospace',
                      }}
                    >
                      /opt/ml/input
                    </span>
                  </Typography>
                ) : (
                  datasets.map((dataset) => (
                    <Typography key={dataset.dataset_uuid} sx={{ mb: 1 }}>
                      Load dataset <b>{dataset.name}</b> into{' '}
                      <span
                        style={{
                          fontFamily: '"Courier New", Courier, monospace',
                        }}
                      >
                        /opt/ml/input/
                        {dataset.name.toLowerCase().replaceAll(' ', '_')}
                      </span>
                    </Typography>
                  ))
                )}
              </Grid>
            ) : null}
            {input_type ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant='h6' gutterBottom>
                  Input
                </Typography>
                {input_type === THIRD_PARTY_TYPES.REGIONAL.value ? (
                  <Typography>
                    Map{' '}
                    <span
                      style={{
                        fontFamily: '"Courier New", Courier, monospace',
                      }}
                    >
                      {input_options?.path}
                    </span>{' '}
                    from regional datastore <b>{input_datastore_name}</b> to{' '}
                    <span
                      style={{
                        fontFamily: '"Courier New", Courier, monospace',
                      }}
                    >
                      /opt/ml/input/
                    </span>
                  </Typography>
                ) : (
                  <Typography>
                    Load <b>{input_uri}</b> from <b>{input_type}</b> into{' '}
                    <span
                      style={{
                        fontFamily: '"Courier New", Courier, monospace',
                      }}
                    >
                      /opt/ml/input/
                    </span>
                  </Typography>
                )}
              </Grid>
            ) : null}
            {output_type ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant='h6' gutterBottom>
                  Output
                </Typography>
                {output_type === 'trainml' ? (
                  <Typography>
                    Create{' '}
                    <b>
                      {import.meta.env.VITE_ORG_NAME}{' '}
                      {output_uri ? output_uri : 'model'}
                    </b>{' '}
                    from contents of{' '}
                    <span
                      style={{
                        fontFamily: '"Courier New", Courier, monospace',
                      }}
                    >
                      /opt/ml/
                      {output_uri && output_uri !== 'model'
                        ? 'output'
                        : 'models'}
                    </span>
                    {output_options && Boolean(output_options.interval)
                      ? ` every ${output_options.interval} minutes.`
                      : null}
                  </Typography>
                ) : output_type === THIRD_PARTY_TYPES.REGIONAL.value ? (
                  <Typography>
                    Save job results from{' '}
                    <span
                      style={{
                        fontFamily: '"Courier New", Courier, monospace',
                      }}
                    >
                      /opt/ml/output
                    </span>{' '}
                    to{' '}
                    <span
                      style={{
                        fontFamily: '"Courier New", Courier, monospace',
                      }}
                    >
                      {output_options?.path}
                    </span>{' '}
                    in regional datastore <b>{output_datastore_name}</b>
                  </Typography>
                ) : (
                  <Typography>
                    Upload results in{' '}
                    <span
                      style={{
                        fontFamily: '"Courier New", Courier, monospace',
                      }}
                    >
                      /opt/ml/output
                    </span>{' '}
                    to <b>{output_uri}</b> on <b>{output_type}</b>
                    {output_options?.archive ? ' (compressed)' : ''}
                  </Typography>
                )}
              </Grid>
            ) : null}
          </Grid>

          {data_volumes && data_volumes.length ? (
            <>
              <Typography variant='h6' sx={{ mt: 2, mb: 1 }}>
                Volumes
              </Typography>
              {data_volumes.map((volume, idx) => {
                const volume_id = volume.volume_id || volume.id;
                const full_volume = volumes.find((v) => v.id === volume_id);
                const volume_name = full_volume
                  ? full_volume.name
                  : volume.name || volume_id;
                const mount_path =
                  data_volumes.length === 1
                    ? '/opt/ml/volume'
                    : `/opt/ml/volume/${volume_name}`;
                return (
                  <Typography key={idx} sx={{ mb: 1 }}>
                    Mount volume <b>{volume_name}</b> to <b>{mount_path}</b>
                  </Typography>
                );
              })}
            </>
          ) : null}

          {data_connectors && data_connectors.length ? (
            <>
              <Typography variant='h6' sx={{ mt: 2, mb: 1 }}>
                Data Connectors
              </Typography>
              {data_connectors.map((connector_id, idx) => {
                const connector = project_data_connectors.find(
                  (c) => c.connector_id === connector_id,
                );
                const connector_name = connector
                  ? connector.name
                  : connector_id;
                return (
                  <Typography key={idx} sx={{ mb: 1 }}>
                    Attach data connector <b>{connector_name}</b>
                  </Typography>
                );
              })}
            </>
          ) : null}

          {private_services && private_services.length ? (
            <>
              <Typography variant='h6' sx={{ mt: 2, mb: 1 }}>
                Private Services
              </Typography>
              {private_services.map((service_id, idx) => {
                const service = project_services.find(
                  (s) => (s.service_id || s.id) === service_id,
                );
                const service_name = service ? service.name : service_id;
                return (
                  <Typography key={idx} sx={{ mb: 1 }}>
                    Attach private service <b>{service_name}</b>
                  </Typography>
                );
              })}
            </>
          ) : null}
        </Paper>
      </Collapse>
    </Box>
  );
}
